import { StoneBedInitialState } from "../reducers/stone-bed-reducer";
import _ from "lodash";
import ManifoldService from "./services/manifold-service";
import { immerable } from "immer";
import { ManifoldUtils } from "./manifolds/utils";

export const ManifoldType = {
  LeftElbow: 0,
  RightElbow: 1,
  StraightHorizontalPipe: 2,
  StraightVerticalPipe: 3,
  TeePipe: 4
};

export const ManifoldStructureType = {
  Round: 0,
  Square: 1,
  Nyloplast: 2
};

export const ManifoldDirection = {
  None: 0,
  Left: 1,
  Right: 2
};

export const ManifoldSpacer = {
  noGap: 1,
  gap: 2
};

/**
 * Main Manifold Class -
 */
export class Manifold{
  [immerable] = true;

  /**
   * @param id {string} - unique identifier
   * @param isInlet {boolean} - true if inlet manifold, otherwise false
   * @param rowOffset
   * @param letRow {number} - The row index this is attached to
   * @param letColumn {number} - The column index this is attached to
   * @param direction {number} - key:1 || 2, text: left || right
   * @param stubsNumber {number} - The number of stubs that will be render on screen
   * @param spacer {number} -  1 = no gap, 2 = gap.
   * @param isElevated {boolean} - true if this is an elevated manifold
   * @param hasWeir {boolean} - true if the manifold structure contains a weir
   * @param isUpper {boolean} - true if this is an upper manifold, false if a lower manifold
   * @param userModified {boolean} - true if user has manually changed the manifold
   * @param manifoldSize {Object} - Manifolds size structure
   * @param manifoldType  {string} - Manifolds type 'upper', 'lower'
   * @param isManifoldInner {boolean} - Manifolds type 'inner', 'outer'
   * @param chambersInUse {[]}
   * @param hasIsolator {boolean}  - Manifolds should have an isolator at its row
   * @param isManifoldInner {boolean} - Manifold is internal to the bed
   * @param hasSecondPipe {boolean} - Manifold structure has 2 direct pipe connections
   * @param refreshManifold
   */
  constructor(
    id, isInlet, rowOffset, letRow, letColumn,
    direction = ManifoldDirection.None,
    stubsNumber, spacer, isElevated, hasWeir,
    isUpper, userModified = false, manifoldSize, manifoldType,
    isManifoldInner = false, chambersInUse,
    hasSecondPipe = false, hasIsolator = true, refreshManifold = false
  ) {
    this._id = id;
    this._isInlet = isInlet;
    this._rowOffset = rowOffset;
    this._letRow = letRow;
    this._letColumn = letColumn;
    this._assembly = [];
    this._direction = direction;
    this._stubsNumber = stubsNumber;
    this._spacer = spacer;
    this._isElevated = isElevated;
    this._hasWeir = hasWeir;
    this._isUpper = isUpper;
    this._userModified = ( userModified ) ? userModified : false;
    this._flowRate = 0;
    this._manifoldSize = manifoldSize;
    this._manifoldType = manifoldType;
    this._isManifoldInner = isManifoldInner;
    this._coordinates = {};
    this._hasSecondPipe = hasSecondPipe;
    this._chambersInUse = chambersInUse || {
      mainRow: [],
      voidRows: []
    };
    this._hasIsolator = hasIsolator;
    this._refreshManifold = refreshManifold;
    this._hasFlipped = false;
  }

  set chambersMainRowInUse( value ) {
    this._chambersInUse.mainRow = value;
  }

  set chambersVoidRowsInUse( value ) {
    this._chambersInUse.voidRows = value;
  }

  get id() {
    return this._id;
  }

  get isInlet() {
    return this._isInlet;
  }

  get isOutlet() {
    return !this._isInlet;
  }

  get rowOffset() {
    return this._rowOffset;
  }

  get letRow() {
    return this._letRow;
  }

  get letColumn() {
    return this._letColumn;
  }

  get initialRow() {
    return this.assembly[0].rowIndex;
  }

  get assembly() {
    return this._assembly;
  }

  set assembly( value ) {
    this._assembly = value;
  }

  get direction() {
    return this._direction;
  }

  set direction( value ) {
    this._direction = value;
  }

  get stubsNumber() {
    return this._stubsNumber;
  }

  set stubsNumber( value ) {
    this._stubsNumber = value;
  }

  get coordinates() {
    return this._coordinates;
  }

  set coordinates( value ) {
    this._coordinates = value;
  }

  get spacer() {
    return this._spacer;
  }

  set spacer( value ) {
    this._spacer = value;
  }

  get isElevated() {
    return this._isElevated;
  }

  set isElevated( value ) {
    this._isElevated = value;
  }

  get hasWeir() {
    return this._hasWeir;
  }

  set hasWeir( value ) {
    this._hasWeir = value;
  }

  get hasFlipped() {
    return this._hasFlipped;
  }

  set hasFlipped( value ) {
    this._hasFlipped = value;
  }

  get isUpper() {
    return this._isUpper;
  }

  set isUpper( value ) {
    this._isUpper = value;
  }

  get chambersInUse() {
    return this._chambersInUse;
  }

  set chambersInUse( value ) {
    this._chambersInUse = value;
  }

  get manifoldType() {
    return this._manifoldType;
  }

  set manifoldType( value ) {
    this._manifoldType = value;
  }

  get isManifoldInner() {
    return this._isManifoldInner;
  }

  set isManifoldInner( value ) {
    this._isManifoldInner = value;
  }

  get userModified() {
    return this._userModified;
  }

  set userModified( value ) {
    this._userModified = value;
  }

  get flowRate() {
    return this._flowRate;
  }

  get manifoldSize() {
    return this._manifoldSize;
  }

  set manifoldSize( value ) {
    this._manifoldSize = value;
  }

  get hasSecondPipe() {
    return this._hasSecondPipe;
  }

  set hasSecondPipe( value ) {
    this._hasSecondPipe = value;
  }

  get hasIsolator() {
    return this._hasIsolator;
  }

  set hasIsolator( booleanValue ) {
    this._hasIsolator = booleanValue;
  }

  get refreshManifold() {
    return this._refreshManifold;
  }

  set refreshManifold( booleanValue ) {
    this._refreshManifold = booleanValue;
  }

  /**
   * Builds the structure in manifold to hold the stubs
   * @param chamberRows
   * @param isRowOffset
   * @param extraInletStubs
   * @param extraOutletStubs
   * @param forceManifoldSpacer
   * @param isUpper
   * @param currentChamberCoordinates
   * @param constructPlacement
   * @param flip
   * @returns {Manifold}
   */
  assemble(
    chamberRows,
    isRowOffset,
    extraInletStubs,
    extraOutletStubs,
    forceManifoldSpacer,
    isUpper,
    currentChamberCoordinates,
    constructPlacement,
    flip = false
  ) {
    this.coordinates = currentChamberCoordinates;

    let firstTimeManifold = false; //Assuming that _isManifoldInner is undefined and only on inner manifolds
    if (this._isManifoldInner && !this._userModified) {
      firstTimeManifold = true;
    }

    if (firstTimeManifold && this._isManifoldInner) {
      this._stubsNumber = constructPlacement["numberOfStartingStubs"];
      extraInletStubs = constructPlacement["numberOfStartingStubs"];
      extraOutletStubs = constructPlacement["numberOfStartingStubs"];
    }

    if (flip) {
      this._direction = ( this._direction === 1 ) ? 2 : 1;
      this._hasFlipped = true;
    }

    let assemblyManifest = this.findPotentialRows(
      chamberRows,
      isRowOffset,
      extraInletStubs,
      extraOutletStubs,
      forceManifoldSpacer,
      isUpper,
      flip
    );

    this._assembly = ManifoldService.getAssembly(assemblyManifest);

    const stubAssemblies = this._assembly
      .filter(manifestPotentialRow => {
        return manifestPotentialRow.structureType === null &&
          manifestPotentialRow.manifoldType !== ManifoldType.StraightHorizontalPipe;
      });

    this._stubsNumber = ( stubAssemblies || [] ).length;

    if (!this._direction) {
      this._direction = !this._isUpper
        ? this.assembly[0].manifoldType === ManifoldType.StraightVerticalPipe ||
        this.assembly[0].manifoldType === ManifoldType.RightElbow
          ? ManifoldDirection.Right
          : ManifoldDirection.Left
        : this.assembly[0].manifoldType === ManifoldType.StraightVerticalPipe ||
        this.assembly[0].manifoldType === ManifoldType.LeftElbow
          ? ManifoldDirection.Left
          : ManifoldDirection.Right;
    }
    if (firstTimeManifold && ( stubAssemblies || [] ).length > 0) this._stubsNumber = constructPlacement["numberOfStartingStubs"];
    return this;
  }

  /**
   * Finds the rows where it will apply the manifolds stubs
   * @param chamberRows {ChamberRow[]} - Chamber Rows Object
   * @param isRowOffset {Number} - Number of Outlet Stubs
   * @param extraInletStubs {Number} - Number of Inlet Stubs
   * @param extraOutletStubs {Number} - Number of Outlet Stubs
   * @param forceManifoldSpacer {Boolean} - Should force adding spacer
   * @param isUpper {Boolean} - Upper or Lower Manifold
   * @param flip
   * @returns {ChamberRow[]}
   */
  findPotentialRows(
    chamberRows,
    isRowOffset,
    extraInletStubs,
    extraOutletStubs,
    forceManifoldSpacer,
    isUpper = true,
    flip = false
  ) {

    let extraStubs = this.isInlet ? extraInletStubs : extraOutletStubs;

    let potentialRows = ManifoldService.findPotentialLimitedRows(chamberRows, isUpper, flip, this);

    let potentialRowAverageRow = potentialRows.reduce(( last, row ) => last + row.row, 0) / potentialRows.length;

    // Lets change the number of stubs
    if (this._userModified && this._stubsNumber >= 0 && this._isManifoldInner) {
      extraStubs = this._stubsNumber;
    }

    // Probably a small bug that resets the number of stubs to zero
    // oct 10 2023
    // https://imaginit.monday.com/boards/232436054/pulses/3320538435
    /*if (this._userModified && this._stubsNumber >= 0 && !this._isInlet) {
       extraStubs = this._stubsNumber;
     }*/

    if (this._userModified && this._stubsNumber >= 0) {
      extraStubs = this._stubsNumber;
    }

    let isForwardDirection = ManifoldService.isForwardDirectionValidate(this, potentialRowAverageRow);

    potentialRows = isForwardDirection ?
      potentialRows :
      potentialRows.reverse();

    // Calculates if its the stubs will be placed on continuous chamber rows.
    // 1 = no spacer, 2 = spaced out.
    let spacer;
    if (this._userModified) {
      spacer = this._spacer === ManifoldSpacer.gap ? ManifoldSpacer.gap : ManifoldSpacer.noGap;
    } else if (forceManifoldSpacer) {
      spacer = ManifoldSpacer.gap;
    } else {
      spacer = (
        extraStubs !== 1 &&
        extraStubs <= Math.ceil(( potentialRows.length - ( isRowOffset ? 2 : 1 ) ) / 2)
      )
        ? ManifoldSpacer.gap
        : ManifoldSpacer.noGap;
    }
    this._spacer = spacer;

    let letStubs = 1;

    // this._stubsNumber = 0;

    // TODO: Only supply the chamber row index at a point; also clean up code
    // Build out assembly manifest
    let { actualRows, thatManifold } = ManifoldService
      .getActualRows(
        potentialRows,
        extraStubs,
        isUpper,
        letStubs,
        isRowOffset,
        spacer,
        this
      );


    // Special case where we force the manifold spacer, but can't put in another row
    // if (forceManifoldSpacer && actualRows.length % spacer === 0) {
    //     actualRows.pop();
    // }

    // // If you have a row offset (skip) and the actualRows.length === 2, remove the last of its assembly
    // if (isRowOffset && actualRows.length === 2) {
    //     actualRows.pop();
    // }

    return isForwardDirection ? actualRows : actualRows.reverse();
  }

  /**
   * Todo check if needed or remove
   *
   * // Build one side of potential rows to run through
   * // Finds all rows that the manifold *could* attach to based on upper/lower flag
   * // Filters out rows without endcaps, missing chambers on the end or different offset values
   * // Determines which side of the outlet row index the manifold should be applied based on the average
   *
   * @param chamberRows
   * @param isUpper
   * @returns {*}
   */
  findPotentialLimitedRows( chamberRows, isUpper ) {
    /**
     * testing its use, if debugger is hit, pleased remove this and add a jsdoc.
     * If not remove after  07-07-21
     */
    console.log("%c <<< findPotentialLimitedRows IN USE >>> ", "background: yellow; color: red");
    debugger;

    /*let reduceFunction = isUpper ? this.simpleFilterReductionUpper.bind(this) : this.simpleFilterReductionLower.bind(this);

    let start = 0, end = 0;
    try {
        start = chamberRows.slice(0, this.letRow + 1).reverse().reduce(reduceFunction, this.letRow);
        end = chamberRows.slice(this.letRow).reduce(reduceFunction, this.letRow);
    } catch (exception) {
        console.warn("Couldn't derive a start or end for manifold");

        start = this.letRow;
        end = this.letRow;
    }

    let potentialRowAverageRow = (start + end) / 2;  // Build an average

    if ((this._direction === ManifoldDirection.Left && !this._isUpper) || (this._direction === ManifoldDirection.Right && this._isUpper)) {
        return chamberRows.slice(this.letRow, end + 1);
    } else if ((this._direction === ManifoldDirection.Right && !this._isUpper) || (this._direction === ManifoldDirection.Left && this._isUpper)) {
        return chamberRows.slice(start, this.letRow + 1);
    } else {
        return potentialRowAverageRow > this.letRow ?
            chamberRows.slice(this.letRow, end + 1) :
            chamberRows.slice(start, this.letRow + 1);
    }*/
  }

  simpleFilterReductionUpper( last, chamberRow, i, array ) {
    //let activeChamber = chamberRow.getFirstActiveChamber(); // Old, only checks for fist and last of the row, we
    // actually need from any col
    let activeChamber = chamberRow.getActiveChamberByCoordinates(this.coordinates);

    if (activeChamber &&
      chamberRow.offSet === this.rowOffset &&
      activeChamber.upperCapActive &&
      activeChamber.col === this.letColumn
    ) return chamberRow.row;

    array.splice(1);
    return last;
  }

  simpleFilterReductionLower( last, chamberRow, i, array ) {
    //let activeChamber = chamberRow.getFirstActiveChamber(); // Old, only checks for fist and last of the row, we
    // actually need from any col
    let activeChamber = chamberRow.getActiveChamberByCoordinates(this.coordinates);

    if (activeChamber &&
      chamberRow.offSet === this.rowOffset &&
      activeChamber.lowerCapActive &&
      activeChamber.col === this.letColumn) {
      return chamberRow.row;
    }

    array.splice(1);
    return last;
  }

    getTrunkSeparation( currentChamberRow, chamberRows, manifoldSeparations, canCollapse, separationDistance = 1 ) {
        try {
            const chamberRow = chamberRows.find(chamberRow => chamberRow.row === this.letRow);

      if (!this.hasChamberRow(currentChamberRow)) {
        return 0;
      }

      if (this.hasIsolator && this._isInlet) {
        if (canCollapse && this._assembly.length === 1) {
          return 0;
        }
        return ManifoldUtils.getTrunkSeparation(this, separationDistance, this.hasIsolator, !canCollapse);
      } else if (this._isOutlet) {
        if (canCollapse && this._assembly.length === 1) {
          return 0;
        }
        return ManifoldUtils.getTrunkSeparation(this, separationDistance, this.hasIsolator, !canCollapse);
      }

      if (canCollapse && this.assembly.length === 1) {
        return 0;
      }
      return ManifoldUtils.getTrunkSeparation(this, separationDistance, this.hasIsolator, !canCollapse);
    } catch (exception) {
      console.warn("An issue occurred while retrieving manifold separation");
    }

    return 0;
  }

  hasChamberRow( chamberRow ) {
    return !!( this.assembly.find(part => part.rowIndex === chamberRow.row) );
  }
}

/**
 * With a Storage volume value and Chamber type, find an appropriate manifold sizing
 *
 * @param storageVolume
 * @param chamberType
 * @returns {*}
 */
export const getManifoldSizing = ( storageVolume, chamberType ) => {
  try {
    return chamberType.manifoldSizing
      .reduce(( last, manifoldSizing ) => manifoldSizing.lowerVolume < storageVolume ? manifoldSizing : last, null);
  } catch (exception) {
    console.warn("At manifolds.js getManifoldSizing() :: Could not find a manifold sizing fitting the storage volume", exception.message);
  }

  return null;
};

/**
 * Obtains the separation value
 * @param structure
 * @param manifoldSizing {Object}
 * @param manifoldPipeSizes
 * @param endCapPipeIndex {Number}
 * @returns {Number}
 */
export const getManifoldStructureCenterSeparation = (
  structure,
  manifoldSizing,
  manifoldPipeSizes,
  endCapPipeIndex = 2
) => {
  try {
    let endCapPipeSize = manifoldPipeSizes.find(
      size => size.pipeSize === manifoldSizing.manifoldSize[endCapPipeIndex]
    );
    let initialSeparation = manifoldSizing.structureDistance;
    let adapterSeparation = 0;
    let pipeSeparation = structure.innerDiameter / 2;

    if (structure.hasAdapter) {
      adapterSeparation = ( endCapPipeSize.adapterOffset + endCapPipeSize.adapterC + 3.0 ) / 12;
    } else {
      adapterSeparation = 0;
    }

    return initialSeparation + adapterSeparation + pipeSeparation;

  } catch (exception) {
    console.warn("Issue occurred when getting separation value", exception);
    return 0;
  }
};

/**
 * Finds and return a manifold separation value
 * The mother of all calculations; will find and return a manifold separation
 * based on the various rules set in the design memos and emails
 *
 * @param structure
 * @param manifoldSizing
 * @param manifoldPipeSizes
 * @param endCapPipeIndex
 * @param separationDistance
 * @param collapsed
 * @returns {*}
 */
export const getManifoldTrunkSeparation = (
  structure,
  manifoldSizing,
  manifoldPipeSizes,
  endCapPipeIndex = 0,
  separationDistance = 0,
  collapsed = false
) => {
  try {
    let endCapPipeSize = manifoldPipeSizes.find(
      size => size.pipeSize === manifoldSizing.manifoldSize[endCapPipeIndex]
    );
    let trunkPipeSize = manifoldPipeSizes.find(
      size => size.pipeSize === manifoldSizing.manifoldSize[1]
    );
    let initialSeparation = manifoldSizing.structureDistance;
    let adapterSeparation = 0;
    let pipeSeparation =
      structure.innerDiameter / 2 +
      trunkPipeSize.pipeOD / 2 +
      getManifoldSeparationOffset(separationDistance, trunkPipeSize.stonePerimeter);

    if (collapsed) {
      pipeSeparation = ( separationDistance - StoneBedInitialState.sepDistance ) / 12;
    } else {
      if (structure.hasAdapter) {
        adapterSeparation = ( endCapPipeSize.adapterOffset + endCapPipeSize.adapterC + 3.0 ) / 12;
      } else if (!collapsed) {
        adapterSeparation = structure.wallThickness;
      }
    }

    const trunkSeparation = initialSeparation + adapterSeparation + pipeSeparation;

    return trunkSeparation;
  } catch (exception) {
    return 0;
  }
};

/**
 * Get the bend separation value if ends of manifolds will be a bend
 *
 * @param chamberType
 * @param chamberRowSpacing
 * @param manifoldSizing
 * @param manifoldPipeSizes
 * @param separationDistance
 * @returns {Number}
 */
export const getManifoldBendSeparation = (
  chamberType,
  chamberRowSpacing,
  manifoldSizing,
  manifoldPipeSizes,
  separationDistance
) => {
  try {
    let endCapPipeSize = manifoldPipeSizes.find(
      size => size.pipeSize === manifoldSizing.manifoldSize[0]
    );
    let trunkPipeSize = manifoldPipeSizes.find(
      size => size.pipeSize === manifoldSizing.manifoldSize[1]
    );

    if (trunkPipeSize.pipeSize !== endCapPipeSize.pipeSize) {
      return 0;
    }

    const initialSeparation = ( chamberType.width + chamberRowSpacing - endCapPipeSize.pipeOD ) / 2;
    const pipeSeparation = getManifoldSeparationOffset(separationDistance, trunkPipeSize.stonePerimeter);
    return initialSeparation - pipeSeparation;
  } catch (exception) {
    return 0;
  }
};

/**
 * Get the end cap separation if the end cap pipe and trunk pipe have differing sizes
 *
 * @param manifoldSizing
 * @param manifoldPipeSizes
 * @param separationDistance
 * @returns {Number}
 */
export const getManifoldEndCapSeparation = ( manifoldSizing, manifoldPipeSizes, separationDistance ) => {
  try {
    let endCapPipeSize = manifoldPipeSizes.find(
      size => size.pipeSize === manifoldSizing.manifoldSize[0]
    );
    let trunkPipeSize = manifoldPipeSizes.find(
      size => size.pipeSize === manifoldSizing.manifoldSize[1]
    );

    if (trunkPipeSize.pipeSize === endCapPipeSize.pipeSize) {
      return 0;
    }

    let initialSeparation = trunkPipeSize.pitch * 3;
    let pipeSeparation = getManifoldSeparationOffset(separationDistance, trunkPipeSize.stonePerimeter);

    return initialSeparation + pipeSeparation;
  } catch (exception) {
    return 0;
  }
};

/**
 * Get the separation offset value given the table value and the override; use the largest of the two
 * @param separationDistance
 * @param otherSeparationDistance
 * @returns {*}
 */
export const getManifoldSeparationOffset = ( separationDistance, otherSeparationDistance ) => {
  // Only use the override if the separation distance is greater than the separation distance provided (from email)
  if (separationDistance > StoneBedInitialState.sepDistance) {
    return Math.max(otherSeparationDistance, separationDistance / 12);
  }
  return otherSeparationDistance;
};

/**
 * Get the manifold structure by the variant value
 *
 * @param string
 * @param manifoldStructures
 * @returns {Object || null}
 */
export const getManifoldStructureByString = ( string, manifoldStructures ) => {
  try {
    switch (string) {
      case "round":
        return manifoldStructures.round[0];
      case "square":
        return manifoldStructures.square[0];
      case "nyloplast":
        return manifoldStructures.nyloplast[0];
      default:
        return null;
    }
  } catch (exception) {
    return null;
  }
};

/**
 * Get the largest manifold trunk separation based on a set of manifolds
 *
 * @param chamberRow
 * @param chamberRows
 * @param manifolds
 * @param manifoldSeparations
 * @param canCollapse
 * @param separationDistance
 * @returns {Object}
 */
export const getLargestManifoldTrunkSeparation = (
  chamberRow,
  chamberRows,
  manifolds,
  manifoldSeparations,
  canCollapse = true,
  separationDistance
) => {
  try {
    return manifolds.reduce(( last, manifold ) =>
      Math.max(last, manifold.getTrunkSeparation(chamberRow, chamberRows, manifoldSeparations, canCollapse, separationDistance)), 0);
  } catch (exception) {
    console.warn("There was an issue calculating the manifold trunk separation");
  }
};

/**
 * Get the manifold bend separation based on a set of manifolds
 *
 * @param chamberRow
 * @param chamberRows
 * @param manifolds
 * @param manifoldSeparations
 * @returns {Array}
 */
export const hasManifoldBendSeparation = ( chamberRow, chamberRows, manifolds, manifoldSeparations ) => {
  try {
    let hasBendSeparation = [];

    manifolds.forEach(manifold => {
      const manifoldPart = manifold.assembly.find(part => part.rowIndex === chamberRow.row);

      if (manifold.assembly.length === 1) {  // Collapsed
        return;
      }

      if (manifoldPart === undefined) {  // Manifold doesn't have part at row
        return;
      }

      if (manifoldPart.structureType !== null) {  // Manifold has a structure, can't have a trunk end cap
        return;
      }

      if (chamberRow.row === manifold.assembly[0].rowIndex) {  // Manifold end cap is placed to the right of the chamber
        if (chamberRow.row > 0 && chamberRows[chamberRow.row - 1].offSet !== chamberRow.offSet) {
          return;
        }

        hasBendSeparation.push(1);
      } else if (chamberRow.row === manifold.assembly[manifold.assembly.length - 1].rowIndex) {  // Manifold end cap is placed to the left of the chamber
        if (chamberRow.row < chamberRows.length - 1 && chamberRows[chamberRow.row + 1].offSet !== chamberRow.offSet) {
          return;
        }

        hasBendSeparation.push(-1);
      }
    });

    if (hasBendSeparation.length === 0) {
      return [];
    }

    hasBendSeparation.sort(( a, b ) => a - b);

    return _.uniq(hasBendSeparation);  // Sort and cull down to the two possible values (left or right)
  } catch (exception) {
    console.warn("There was an issue in calculating the actual manifold separation");
  }
};

/**
 * Get the manifold end cap separation based on a set of manifolds
 *
 * @param chamberRow
 * @param chamberRows
 * @param manifolds
 * @param manifoldSeparations
 * @returns {*}
 */
export const hasManifoldEndCapSeparation = ( chamberRow, chamberRows, manifolds, manifoldSeparations ) => {
  try {

    let hasEndCapSeparation = [];

    manifolds.forEach(manifold => {
      const manifoldPart = manifold.assembly.find(part => part.rowIndex === chamberRow.row);

      if (
        manifold.assembly.length === 1 ||  // Collapsed
        manifold.assembly.length === 2 ||  // Two bends
        ( manifold.assembly.length === 3 && manifold.assembly[1].manifoldType === ManifoldType.StraightHorizontalPipe )  // Two bends and a horizontal pipe
      ) {
        return;
      }

      if (manifoldPart === undefined) {  // Manifold doesn't have part at row
        return;
      }

      if (manifoldPart.structureType !== null) {  // Manifold has a structure, can't have a trunk end cap
        return;
      }

      if (chamberRow.row === manifold.assembly[0].rowIndex) {  // Manifold end cap is placed to the right of the chamber
        hasEndCapSeparation.push(1);
      } else if (chamberRow.row === manifold.assembly[manifold.assembly.length - 1].rowIndex) {  // Manifold end cap is placed to the left of the chamber
        hasEndCapSeparation.push(-1);
      }
    });

    if (hasEndCapSeparation.length === 0) {
      return [];
    }

    hasEndCapSeparation.sort(( a, b ) => a - b);

    return _.uniq(hasEndCapSeparation);  // Sort and cull down to the two possible values (left or right)
  } catch (exception) {
    console.warn("There was an issue in calculating the actual manifold separation");
  }
};



// WEBPACK FOOTER //
// ./src/lib/manifolds.js